import React, { useState, useEffect, useMemo } from "react";
import VulnerabilityForm from "./VulnerabilityForm";
import ConfirmationModal from "./ConfirmationModal";
import { api, apiEndpoints } from "../api/api";
import "./AssessmentDetails.css";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import RetestForm from "./RetestForm";

// --- CHARTS ---
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useNavigate } from "react-router-dom";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

// Define severity map here to be accessible throughout the component
const severityMap = {
  1: "Critical",
  2: "High",
  3: "Medium",
  4: "Low",
  5: "Info",
};

const AssessmentDetails = ({
  assessment,
  onBack,
  onEditAssessment,
  userRoles, // This prop is used to determine the role
  currentUser,
}) => {
  // --- Your Original States ---
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [loadingVulnerabilities, setLoadingVulnerabilities] = useState(true);
  const [errorVulnerabilities, setErrorVulnerabilities] = useState(null);
  const navigate = useNavigate();
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: "ascending",
  });
  const [showVulnerabilityForm, setShowVulnerabilityForm] = useState(false);
  const [currentVulnerabilityToEdit, setCurrentVulnerabilityToEdit] =
    useState(null);
  const [allAssessmentVersions, setAllAssessmentVersions] = useState([]);
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [showVersionSelectionMessage, setShowVersionSelectionMessage] =
    useState(false);
  const [currentAssessment, setCurrentAssessment] = useState(assessment);
  const [workflowMessage, setWorkflowMessage] = useState({
    type: "",
    text: "",
  });
  const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);

  // Your Original useEffect to sync props
  useEffect(() => {
    setCurrentAssessment(assessment);
  }, [assessment]);

  // Your fetch logic is preserved, with added logic to merge data
  const fetchAssessmentVersions = async () => {
    if (!currentAssessment || !currentAssessment.assessmentId) {
      console.warn("Assessment ID not available for fetching versions.");
      setAllAssessmentVersions([]);
      setSelectedVersion(null);
      return;
    }

    try {
      const url = apiEndpoints.assessmentVersionsByAssessmentId(
        currentAssessment.assessmentId
      );
      const response = await api.get(url);

      const fetchedVersions = response.data;
      const completeVersionsFromProp = assessment?.versions || [];

      const enrichedVersions = fetchedVersions.map((fetchedVersion) => {
        const completeVersionData = completeVersionsFromProp.find(
          (propVersion) =>
            propVersion.assessmentVersionId ===
            fetchedVersion.assessmentVersionId
        );
        return {
          ...fetchedVersion,
          ...completeVersionData,
        };
      });

      setAllAssessmentVersions(enrichedVersions);

      if (enrichedVersions.length > 0) {
        // ✅ CHANGE: Default to the latest version on load
        const latest = enrichedVersions.sort(
          (a, b) => parseFloat(b.versionId || 0) - parseFloat(a.versionId || 0)
        )[0];
        setSelectedVersion(latest);
      } else {
        setSelectedVersion(null);
      }
    } catch (error) {
      console.error("Failed to fetch assessment versions:", error);
      setAllAssessmentVersions([]);
      setSelectedVersion(null);
    }
  };

  // Your original fetch logic for vulnerabilities
  const fetchVulnerabilitiesForSelectedVersion = async () => {
    if (!selectedVersion) {
      setVulnerabilities([]);
      setLoadingVulnerabilities(false);
      return;
    }

    setLoadingVulnerabilities(true);
    setErrorVulnerabilities(null);

    try {
      const url = apiEndpoints.vulnerabilitiesByVersion(
        currentAssessment.assessmentId,
        selectedVersion.assessmentVersionId
      );
      console.log(`Fetching vulnerabilities from: ${url}`);
      const response = await api.get(url);
      setVulnerabilities(response.data);
    } catch (err) {
      setErrorVulnerabilities(
        err.response?.data?.message || err.message || "An error occurred."
      );
      setVulnerabilities([]);
      console.error("Failed to fetch vulnerabilities:", err);
    } finally {
      setLoadingVulnerabilities(false);
    }
  };

  const handleRetestSuccess = () => {
    window.alert(
      "Retest version created successfully. The assessment is now 'In Progress'."
    );
    navigate("/dashboard");
  };

  // Your original useEffect hooks are preserved
  useEffect(() => {
    fetchAssessmentVersions();
    // eslint-disable-next-line
  }, [currentAssessment]);

  useEffect(() => {
    if (selectedVersion) {
      fetchVulnerabilitiesForSelectedVersion();
    }
    // eslint-disable-next-line
  }, [selectedVersion]);

  const requestSort = (key) => {
    let direction = "ascending";
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending";
    }
    setSortConfig({ key, direction });
  };

  const sortedVulnerabilities = useMemo(() => {
    let sortableVulnerabilities = [...vulnerabilities];
    if (sortConfig.key) {
      sortableVulnerabilities.sort((a, b) => {
        const aValue = a[sortConfig.key] || "";
        const bValue = b[sortConfig.key] || "";
        if (sortConfig.key === "severityId" || typeof aValue === "number") {
          if (aValue < bValue)
            return sortConfig.direction === "ascending" ? -1 : 1;
          if (aValue > bValue)
            return sortConfig.direction === "ascending" ? 1 : -1;
          return 0;
        }
        if (typeof aValue === "string" && typeof bValue === "string") {
          return sortConfig.direction === "ascending"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        }
        return 0;
      });
    }
    return sortableVulnerabilities;
  }, [vulnerabilities, sortConfig]);

  const currentStatus = currentAssessment.status?.name || "Unknown";
  const isClosed = currentStatus.toLowerCase() === "closed";

  const { barChartData, doughnutChartData } = useMemo(() => {
    const severityCounts = {
      Critical: 0,
      High: 0,
      Medium: 0,
      Low: 0,
      Info: 0,
    };
    const categoryCounts = {};
    vulnerabilities.forEach((vuln) => {
      if (!isClosed) {
        const severityName = severityMap[vuln.severityId] || "Info";
        if (severityName in severityCounts) {
          severityCounts[severityName]++;
        }
      }
      const category = vuln.category || "Uncategorized";
      categoryCounts[category] = (categoryCounts[category] || 0) + 1;
    });
    const barChartData = {
      labels: ["Critical", "High", "Medium", "Low", "Info"],
      datasets: [
        {
          label: "Vulnerability Count",
          data: isClosed
            ? [0, 0, 0, 0, 0]
            : [
                severityCounts.Critical,
                severityCounts.High,
                severityCounts.Medium,
                severityCounts.Low,
                severityCounts.Info,
              ],
          backgroundColor: [
            "#B00020",
            "#EF6C00",
            "#FDD835",
            "#4CAF50",
            "#2196F3",
          ],
          borderColor: "rgba(0,0,0,0.2)",
          borderWidth: 1,
        },
      ],
    };
    const doughnutLabels = Object.keys(categoryCounts);
    const doughnutValues = Object.values(categoryCounts);
    const doughnutChartData = {
      labels: doughnutLabels,
      datasets: [
        {
          label: "# of Vulnerabilities",
          data: doughnutValues,
          backgroundColor: [
            "#36A2EB",
            "#FFCE56",
            "#FF6384",
            "#4BC0C0",
            "#9966FF",
            "#FF9F40",
            "#C9CBCF",
          ].slice(0, Math.max(doughnutValues.length, 1)),
          borderColor: "rgba(255,255,255,0.9)",
          borderWidth: 1,
        },
      ],
    };
    return { barChartData, doughnutChartData };
  }, [vulnerabilities, isClosed]);

  const barChartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
      datalabels: {
        display: !isClosed,
        anchor: "end",
        align: "right",
        color: "#000",
        clamp: true,
        formatter: (value) => (value ? value : ""),
        font: {
          weight: "bold",
        },
      },
    },
    scales: {
      y: {
        title: {
          display: true,
          text: "Risk Rating",
          color: "#dc3545",
          font: {
            size: 14,
            weight: "bold",
          },
        },
        grid: {
          display: false,
        },
        labels: ["Critical", "High", "Medium", "Low", "Info"],
      },
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 2,
          display: !isClosed,
        },
        grid: {
          display: !isClosed,
        },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0) || 1;
            const value = context.dataset.data[context.dataIndex] || 0;
            const percentage = ((value / total) * 100).toFixed(0);
            return `${context.label}: ${percentage}%`;
          },
        },
      },
      datalabels: {
        color: "#fff",
        formatter: (value, context) => {
          const total =
            context.chart.data.datasets[0].data.reduce((a, b) => a + b, 0) || 1;
          const percentage = ((value / total) * 100).toFixed(0);
          return value ? `${percentage}%` : "";
        },
        font: {
          weight: "bold",
        },
      },
    },
    cutout: "55%",
  };

  const handleAddVulnerabilityClick = () => {
    if (!selectedVersion) {
      setShowVersionSelectionMessage(true);
      setTimeout(() => setShowVersionSelectionMessage(false), 3000);
      return;
    }
    setCurrentVulnerabilityToEdit(null);
    setShowVulnerabilityForm(true);
  };

  const handleViewEditVulnerabilityClick = (vulnerability) => {
    setCurrentVulnerabilityToEdit(vulnerability);
    setShowVulnerabilityForm(true);
  };

  const handleVulnerabilitySaved = () => {
    setShowVulnerabilityForm(false);
    setCurrentVulnerabilityToEdit(null);
    fetchAssessmentVersions(); // Refresh versions to get latest data after save
  };

  const handleCancelVulnerabilityForm = () => {
    setShowVulnerabilityForm(false);
    setCurrentVulnerabilityToEdit(null);
  };

  const handleDownloadExcel = async () => {
    if (!selectedVersion) {
      alert("Please select a version to download the report.");
      return;
    }
    try {
      const workbook = new ExcelJS.Workbook();
      const response = await fetch("/penetration_template.xlsx");
      const buffer = await response.arrayBuffer();
      await workbook.xlsx.load(buffer);
      const sheet1 = workbook.getWorksheet("Scope and Dashboard");
      if (sheet1) {
        sheet1.getCell("D2").value = assessment.clientName || "";
        sheet1.getCell("D3").value = assessment.activityName || "";
        sheet1.getCell("D4").value = assessment.scopeDetails || "";
        sheet1.getCell("D5").value = assessment.outOfScope || "";
        sheet1.getCell("D6").value = assessment.assessmentStartDate || "";
        sheet1.getCell("D7").value = assessment.assessmentEndDate || "";
        sheet1.getCell("D8").value = selectedVersion?.reportReleaseDate || "";
        sheet1.getCell("D9").value = Array.isArray(selectedVersion?.preparedBy)
          ? selectedVersion.preparedBy.join(", ")
          : selectedVersion?.preparedBy || "";
        sheet1.getCell("D10").value = selectedVersion?.reviewedBy || "";
        sheet1.getCell("D11").value = selectedVersion?.approvedBy || "";
        sheet1.getCell("D12").value = selectedVersion?.sdm || "";
      }
      const sheet2 = workbook.getWorksheet("Detailed Findings");
      if (sheet2) {
        const startRow = 5;
        vulnerabilities.forEach((v, i) => {
          const row = sheet2.getRow(startRow + i);
          row.getCell(1).value = i + 1;
          row.getCell(2).value = v.testId || "";
          row.getCell(3).value = Array.isArray(v.urls)
            ? v.urls.join(", ")
            : v.urls || "";
          row.getCell(4).value = v.vulnId || "";
          row.getCell(5).value = v.vulnTitle || "";
          row.getCell(6).value = severityMap[v.severityId] || "Unknown";
          row.getCell(7).value = v.category || "";
          row.getCell(8).value = v.attributingFactor || "";
          row.getCell(9).value = v.description || "";
          row.getCell(10).value = v.businessImpact || "";
          row.getCell(11).value = v.recommendations || "";
          row.getCell(12).value = v.cve || "";
          row.getCell(13).value = v.cwe || "";
          row.getCell(14).value = v.patchingStatus || "";
          row.getCell(15).value = v.commentsByClient || "";
          row.getCell(16).value = v.commentsByVarutraTeam || "";
          row.commit();
        });
      }
      const sheet3 = workbook.getWorksheet("Supporting Evidence");
      if (sheet3) {
        const startRow = 5;
        vulnerabilities.forEach((v, i) => {
          const row = sheet3.getRow(startRow + i);
          row.getCell(1).value = i + 1;
          row.getCell(2).value = v.vulnId || "";
          row.getCell(3).value = v.vulnTitle || "";
          row.getCell(4).value = v.proofOfConcept || "See Screenshot";
          if (v.screenshots && Array.isArray(v.screenshots)) {
            v.screenshots.forEach((s) => {
              if (s.base64) {
                const imageId = workbook.addImage({
                  base64: s.base64,
                  extension: "png",
                });
                sheet3.addImage(imageId, {
                  tl: { col: 4, row: startRow + i },
                  ext: { width: 200, height: 150 },
                });
              }
            });
          }
          row.commit();
        });
      }
      const sheet4 = workbook.getWorksheet("Appendix-Risk Rating Metrics");
      if (sheet4) {
        const startRow = 3;
        const metrics = [
          ["Critical", "CVSS >= 9.0"],
          ["High", "7.0 – 8.9"],
          ["Medium", "4.0 – 6.9"],
          ["Low", "< 4.0"],
        ];
        metrics.forEach((m, i) => {
          const row = sheet4.getRow(startRow + i);
          row.getCell(1).value = m[0];
          row.getCell(2).value = m[1];
          row.commit();
        });
      }
      const updatedBuffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([updatedBuffer]), "Penetration Test Report.xlsx");
    } catch (error) {
      console.error("Error generating Excel:", error);
      alert("Failed to generate Excel report. Check console for details.");
    }
  };

  const handleWorkflowAction = async (action) => {
    if (action === "close") {
      setShowCloseConfirmModal(false);
    }
    const payload = {
      assessmentId: assessment.assessmentId,
      action,
    };
    try {
      const res = await api.post(apiEndpoints.workflowUpdate, payload);
      setCurrentAssessment(res.data.assessment);
      const successMessage = `Action '${action}' successful!`;
      setWorkflowMessage({ type: "success", text: successMessage });
      window.alert(successMessage);
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Workflow update failed.";
      setWorkflowMessage({ type: "error", text: errorMessage });
      window.alert(`Workflow update failed: ${errorMessage}`);
    } finally {
      setTimeout(() => setWorkflowMessage({ type: "", text: "" }), 5000);
    }
  };

  // Find the latest version using useMemo for efficiency
  const latestVersion = useMemo(() => {
    if (!allAssessmentVersions || allAssessmentVersions.length === 0)
      return null;
    // Create a shallow copy and sort descending by versionId to find the latest
    return [...allAssessmentVersions].sort(
      (a, b) => parseFloat(b.versionId || 0) - parseFloat(a.versionId || 0)
    )[0];
  }, [allAssessmentVersions]);

  if (!currentAssessment || !currentAssessment.assessmentId) {
    return (
      <div className="container card-loading">
        Loading assessment details...
      </div>
    );
  }

  // ✅ --- ENTIRE PERMISSION LOGIC BLOCK UPDATED ---

  // 1. Define Roles
  const isManagerialRole =
    userRoles?.includes("AssisstantManager") || userRoles?.includes("Manager");
  const isClient = userRoles?.includes("Client");
  const isTester = userRoles?.includes("Tester");
  const isTeamLead = userRoles?.includes("TeamLead");

  // 2. Define Statuses
  const isL2Approved = currentStatus.toLowerCase() === "l2 approved";
  const isL1Waiting = currentStatus.toLowerCase() === "l1 waiting";
  const isInProgress = currentStatus.toLowerCase() === "in progress";

  // 3. Define Version & Assignment Checks
  const isLatestVersionSelected =
    selectedVersion &&
    latestVersion &&
    selectedVersion.assessmentVersionId === latestVersion.assessmentVersionId;

  const isTesterAssignedToSelectedVersion =
    isTester &&
    currentUser?.name &&
    Array.isArray(selectedVersion?.preparedBy) &&
    selectedVersion.preparedBy.includes(currentUser.name);

  const isTeamLeadAssignedToSelectedVersion =
    isTeamLead &&
    currentUser?.name &&
    selectedVersion?.reviewedBy === currentUser.name;

  // 4. Define granular permissions for adding/editing vulnerabilities
  // A non-manager can only edit if they are assigned AND it's the LATEST version.
  const canTesterEdit =
    isTesterAssignedToSelectedVersion &&
    isInProgress &&
    isLatestVersionSelected;
  const canTeamLeadEdit =
    isTeamLeadAssignedToSelectedVersion &&
    (isInProgress || isL1Waiting) &&
    isLatestVersionSelected;

  // A user can edit vulnerabilities in the selected version if they are a manager OR meet the specific role/status/version criteria.
  const canEditSelectedVersion =
    isManagerialRole || canTesterEdit || canTeamLeadEdit;

  // 5. Define final button disable states
  const disableEditAssessment = !isManagerialRole; // Only managers can edit core assessment details

  // The "Add Vulnerability" button is disabled if the user doesn't have edit rights on the currently selected version.
  const disableAddVulnerability = !canEditSelectedVersion;

  return (
    <div className="container card">
      {showCloseConfirmModal && (
        <ConfirmationModal
          message="Are you sure you want to close this assessment?"
          onConfirm={() => handleWorkflowAction("close")}
          onCancel={() => setShowCloseConfirmModal(false)}
        />
      )}
      <h1 className="assessment-details-title">
        Assessment Details:{" "}
        {currentAssessment.assessmentName || currentAssessment.activityName}
      </h1>
      <div className="status-section">
        <h3>
          Current Status:
          <span
            className={`status-badge status-${currentStatus
              .toLowerCase()
              .replace(/\s+/g, "-")}`}
          >
            {currentStatus}
          </span>
        </h3>
        {workflowMessage.text && (
          <div className={`workflow-message ${workflowMessage.type}`}>
            {workflowMessage.text}
          </div>
        )}
      </div>
      {isManagerialRole && currentStatus.toLowerCase() === "sent to retest" ? (
        <RetestForm
          assessmentId={currentAssessment.assessmentId}
          onRetestSuccess={handleRetestSuccess}
        />
      ) : showVulnerabilityForm ? (
        <VulnerabilityForm
          selectedAssessment={currentAssessment}
          selectedVersion={selectedVersion}
          selectedVulnerability={currentVulnerabilityToEdit}
          onSave={handleVulnerabilitySaved}
          onClose={handleCancelVulnerabilityForm}
          userRoles={userRoles}
          currentUser={currentUser}
          assessmentStatus={currentStatus}
          // ✅ NEW PROP: Tell the form if it should be read-only.
          // This is based on the user's role and whether the selected version is the latest.
          // NOTE: This requires the VulnerabilityForm component to accept and use this prop.
          isReadOnly={!canEditSelectedVersion}
        />
      ) : (
        <>
          <form id="assessmentDetailsForm" className="form-section">
            <div className="form-grid-columns">
              <div className="form-group">
                <label htmlFor="detailsActivityName" className="form-label">
                  Activity Name
                </label>
                <input
                  type="text"
                  id="detailsActivityName"
                  className="form-input form-input-readonly"
                  value={currentAssessment.activityName || ""}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="detailsScopeDetails" className="form-label">
                  Scope Details
                </label>
                <textarea
                  id="detailsScopeDetails"
                  className="form-textarea form-textarea-readonly"
                  value={currentAssessment.scopeDetails || ""}
                  readOnly
                ></textarea>
              </div>
              <div className="form-group">
                <label htmlFor="detailsOutOfScope" className="form-label">
                  Out of Scope
                </label>
                <textarea
                  id="detailsOutOfScope"
                  className="form-textarea form-textarea-readonly"
                  value={currentAssessment.outOfScope || ""}
                  readOnly
                ></textarea>
              </div>
              <div className="form-group-date-grid">
                <div>
                  <label
                    htmlFor="detailsAssessmentStartDate"
                    className="form-label"
                  >
                    Assessment Start Date
                  </label>
                  <input
                    type="date"
                    id="detailsAssessmentStartDate"
                    className="form-input form-input-readonly"
                    value={currentAssessment.assessmentStartDate || ""}
                    readOnly
                  />
                </div>
                <div>
                  <label
                    htmlFor="detailsAssessmentEndDate"
                    className="form-label"
                  >
                    Assessment End Date
                  </label>
                  <input
                    type="date"
                    id="detailsAssessmentEndDate"
                    className="form-input form-input-readonly"
                    value={currentAssessment.assessmentEndDate || ""}
                    readOnly
                  />
                </div>
                <div className="form-group-span-2">
                  <label
                    htmlFor="detailsReportReleaseDate"
                    className="form-label"
                  >
                    Report Release Date
                  </label>
                  <input
                    type="date"
                    id="detailsReportReleaseDate"
                    className="form-input form-input-readonly"
                    value={selectedVersion?.reportReleaseDate || ""}
                    readOnly
                  />
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="detailsPreparedBy" className="form-label">
                  Prepared by
                </label>
                <input
                  type="text"
                  id="detailsPreparedBy"
                  className="form-input form-input-readonly"
                  value={
                    Array.isArray(selectedVersion?.preparedBy)
                      ? selectedVersion.preparedBy.join(", ")
                      : selectedVersion?.preparedBy || ""
                  }
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="detailsReviewedBy" className="form-label">
                  Reviewed by
                </label>
                <input
                  type="text"
                  id="detailsReviewedBy"
                  className="form-input form-input-readonly"
                  value={selectedVersion?.reviewedBy || ""}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="detailsApprovedBy" className="form-label">
                  Approved by
                </label>
                <input
                  type="text"
                  id="detailsApprovedBy"
                  className="form-input form-input-readonly"
                  value={selectedVersion?.approvedBy || ""}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="detailsSdm" className="form-label">
                  SDM
                </label>
                <input
                  type="text"
                  id="detailsSdm"
                  className="form-input form-input-readonly"
                  value={selectedVersion?.sdm || ""}
                  readOnly
                />
              </div>
            </div>
            <h3 className="form-section-title">Document Properties</h3>
            <div className="form-grid-columns-3">
              <div className="form-group">
                <label htmlFor="detailsDocVersion" className="form-label">
                  Version
                </label>
                <input
                  type="text"
                  id="detailsDocVersion"
                  className="form-input form-input-readonly"
                  value={selectedVersion?.versionId || ""}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="detailsDocDate" className="form-label">
                  Date
                </label>
                <input
                  type="date"
                  id="detailsDocDate"
                  className="form-input form-input-readonly"
                  value={selectedVersion?.date || ""}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label htmlFor="detailsDocDescription" className="form-label">
                  Description
                </label>
                <input
                  type="text"
                  id="detailsDocDescription"
                  className="form-input form-input-readonly"
                  value={selectedVersion?.description || ""}
                  readOnly
                />
              </div>
            </div>
            <h3 className="form-section-title">PRIVACY OF CONTENT</h3>
            <div className="form-group">
              <p className="form-textarea-readonly">
                The information contained herein is considered privileged,
                confidential and the property of VARUTRA and
                <span
                  style={{
                    fontWeight: "bold",
                    color: "blue",
                    marginLeft: "5px",
                  }}
                >
                  {assessment?.clientName || "Client"}
                </span>
                . This internal report, including any reproduction portions of
                this report, contains conclusions and analysis that reflect the
                information available to Varutra prior to the completion date of
                the work. Upon delivery, Varutra has not continued to monitor,
                nor does the report reflect, any later alterations that may have
                been made to the subject matter of this report.
              </p>
            </div>
            <h3 className="form-section-title">
              Vulnerability Statistics Details
            </h3>
            <div className="charts-container">
              {isClosed && vulnerabilities.length === 0 ? (
                <div
                  style={{
                    height: "300px",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    border: "1px solid #ccc",
                    borderRadius: "8px",
                    textAlign: "center",
                  }}
                >
                  <p style={{ color: "#888", margin: "0" }}>
                    No Vulnerability Data Available
                  </p>
                  <p style={{ color: "#888", fontSize: "0.8em" }}>
                    Assessment Status: Closed
                  </p>
                </div>
              ) : (
                <div className="chart-wrapper">
                  <Bar options={barChartOptions} data={barChartData} />
                  <span className="chart-title">Fig: Vulnerability Count</span>
                </div>
              )}
              <div className="chart-wrapper">
                <Doughnut
                  options={doughnutChartOptions}
                  data={doughnutChartData}
                />
                <span className="chart-title">
                  Fig: Vulnerability Categories
                </span>
              </div>
            </div>
          </form>

          <h2 className="vulnerabilities-section-title">Vulnerabilities</h2>

          {isClosed ? (
            <div
              className="no-data-message"
              style={{ textAlign: "center", padding: "1rem" }}
            >
              This assessment is closed. No vulnerabilities to display.
            </div>
          ) : (
            <>
              <div className="version-select-container">
                <label htmlFor="assessmentVersionSelect" className="form-label">
                  Select Version:
                </label>
                <select
                  id="assessmentVersionSelect"
                  className="form-select"
                  value={selectedVersion?.assessmentVersionId || ""}
                  onChange={(e) => {
                    const version = allAssessmentVersions.find(
                      (v) => v.assessmentVersionId === e.target.value
                    );
                    setSelectedVersion(version);
                  }}
                  disabled={allAssessmentVersions.length === 0}
                >
                  <option value="">-- Select a Version --</option>
                  {allAssessmentVersions.map((version) => (
                    <option
                      key={version.assessmentVersionId}
                      value={version.assessmentVersionId}
                    >
                      {`Version ${version.versionId || "N/A"} (${
                        version.date || "No Date"
                      })`}
                    </option>
                  ))}
                </select>
              </div>

              {showVersionSelectionMessage && (
                <div className="version-selection-message">
                  Please select an assessment version from the dropdown above
                  before adding a vulnerability.
                </div>
              )}

              {loadingVulnerabilities ? (
                <div className="loading-message">
                  Loading vulnerabilities...
                </div>
              ) : errorVulnerabilities ? (
                <div className="error-message">
                  Error loading vulnerabilities: {errorVulnerabilities}
                </div>
              ) : (
                <div className="table-container">
                  <table className="vulnerability-table">
                    <thead>
                      <tr className="table-header">
                        <th
                          className="table-header-cell sortable"
                          onClick={() => requestSort("vulnId")}
                        >
                          Vulnerability ID
                        </th>
                        <th
                          className="table-header-cell sortable"
                          onClick={() => requestSort("vulnTitle")}
                        >
                          Vulnerability Title
                        </th>
                        <th
                          className="table-header-cell sortable"
                          onClick={() => requestSort("severityId")}
                        >
                          Severity
                        </th>
                        <th
                          className="table-header-cell sortable"
                          onClick={() => requestSort("patchingStatus")}
                        >
                          Status
                        </th>
                        <th className="table-header-cell">Actions</th>
                      </tr>
                    </thead>
                    <tbody id="vulnerabilityTableBody" className="table-body">
                      {sortedVulnerabilities.length > 0 ? (
                        sortedVulnerabilities.map((vulnerability) => (
                          <tr key={vulnerability.vulnId} className="table-row">
                            <td className="table-cell">
                              {vulnerability.vulnId}
                            </td>
                            <td className="table-cell">
                              {vulnerability.vulnTitle}
                            </td>
                            <td className="table-cell">
                              {severityMap[vulnerability.severityId] ||
                                "Unknown"}
                            </td>
                            <td className="table-cell">
                              {vulnerability.patchingStatus}
                            </td>
                            <td className="table-cell table-actions">
                              <button
                                onClick={() =>
                                  handleViewEditVulnerabilityClick(
                                    vulnerability
                                  )
                                }
                                className="action-button edit-button"
                              >
                                {/* ✅ CHANGE: Button text is now dynamic for better UX */}
                                {canEditSelectedVersion ? "View/Edit" : "View"}
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="5" className="no-data-message">
                            No vulnerabilities found for this assessment
                            version.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

          {/* ✅ UPDATED: Workflow buttons now check for assignment */}
          {!isClient && (
            <div className="workflow-actions">
              <h4>Workflow Actions</h4>
              <div className="button-group">
                {currentStatus === "In Progress" &&
                  (isTesterAssignedToSelectedVersion || isManagerialRole) && (
                    <button
                      onClick={() => handleWorkflowAction("submit")}
                      className="btn-workflow submit"
                    >
                      Submit Assessment
                    </button>
                  )}
                {currentStatus.toLowerCase() === "l1 waiting" &&
                  (isTeamLeadAssignedToSelectedVersion || isManagerialRole) && (
                    <>
                      <button
                        onClick={() => handleWorkflowAction("l1approve")}
                        className="btn-workflow l1-approve"
                      >
                        L1 Approve
                      </button>
                      <button
                        onClick={() => handleWorkflowAction("reject")}
                        className="btn-workflow reject"
                      >
                        Reject
                      </button>
                    </>
                  )}
                {currentStatus.toLowerCase() === "l1 approved" &&
                  isManagerialRole && (
                    <>
                      <button
                        onClick={() => handleWorkflowAction("l2approve")}
                        className="btn-workflow l2-approve"
                      >
                        L2 Approve
                      </button>
                      <button
                        onClick={() => handleWorkflowAction("reject")}
                        className="btn-workflow reject"
                      >
                        Reject
                      </button>
                    </>
                  )}
                {isManagerialRole && isL2Approved && (
                  <button
                    onClick={() => setShowCloseConfirmModal(true)}
                    className="btn-workflow close-assessment"
                  >
                    Close Assessment
                  </button>
                )}
              </div>
            </div>
          )}

          {isClient && isL2Approved && (
            <div className="workflow-actions">
              <h4>Client Actions</h4>
              <div className="button-group">
                <button
                  onClick={() => handleWorkflowAction("retest")}
                  className="btn-workflow retest"
                >
                  Send for Retest
                </button>
              </div>
            </div>
          )}

          <div className="bottom-buttons">
            <button
              onClick={() => navigate("/dashboard")}
              className="btn btn-secondary"
              style={{ marginRight: "10px" }}
            >
              Back to Dashboard
            </button>
          </div>
          <div className="flex justify-end space-x-4">
            {!isClient && (
              <button
                onClick={() => onEditAssessment(currentAssessment)}
                className="btn btn-primary"
                disabled={disableEditAssessment}
              >
                Edit Assessment
              </button>
            )}
            <button
              onClick={handleAddVulnerabilityClick}
              className="btn btn-success"
              disabled={disableAddVulnerability}
            >
              Add New Vulnerability
            </button>
            <button
              className="btn btn-success"
              style={{ marginRight: "10px" }}
              onClick={handleDownloadExcel}
            >
              Download
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AssessmentDetails;
